import React, { useState, useMemo, useEffect } from 'react';
import { ethers } from 'ethers';
import { Sparkles, Fuel, Zap, AlertTriangle, ArrowRightLeft, Wand2 } from 'lucide-react';
import artifact from '../contracts/CoinForgeToken.json';
import { useWallet } from '../context/WalletContext';
import ImageDropzone from '../components/ImageDropzone';
import ProgressModal from '../components/ProgressModal';
import SuccessModal from '../components/SuccessModal';
import CreatorEconomicsModal from '../components/CreatorEconomicsModal';
import { uploadImage, saveToken } from '../lib/api';
import { networkOf, hasLpSupport, txUrl, DEFAULT_CHAIN_ID, CHAIN_PARAMS, LAUNCH_CHAINS } from '../lib/networks';
import { fireLaunchConfetti } from '../lib/celebrate';
import { playPop, playLaunchFanfare } from '../lib/sounds';
import { waitForBytecode, hasEnoughForSeed } from '../lib/lpRecovery';
import LpRecoveryPanel from '../components/LpRecoveryPanel';
import { toast } from 'sonner';

const INITIAL_ETH = '0.01'; // fixed ETH attached for LP seeding

// Quick-launch presets: one click pre-fills the whole form.
const PRESETS = [
  { label: 'Doge Classic', name: 'Doge Forge', symbol: 'DOGEF', supply: '1000000000' },
  { label: 'Moon Shot', name: 'Moon Shot', symbol: 'MOON', supply: '100000000' },
  { label: 'Pixel Pepe', name: 'Pixel Pepe', symbol: 'PPEPE', supply: '420690000' },
  { label: 'Based Cat', name: 'Based Cat', symbol: 'BCAT', supply: '1000000' },
];

const Dashboard = () => {
  const { wallet, connect } = useWallet();
  const [form, setForm] = useState({ name: '', symbol: '', supply: '1000000' });
  const [image, setImage] = useState(null);
  const [gasEstimate, setGasEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // If tx 1 (deploy) succeeds but tx 2 (seedLiquidity) fails, we hold the deployed
  // token here so the creator can retry LP seeding without leaving the page.
  const [partial, setPartial] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const applyPreset = (p) => {
    setForm({ name: p.name, symbol: p.symbol, supply: p.supply });
    playPop();
    toast.success(`${p.label} preset loaded — just hit Launch`);
  };
  const symbolUpper = useMemo(() => form.symbol.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11), [form.symbol]);
  const canEstimate = wallet && form.name.trim() && symbolUpper && Number(form.supply) > 0;

  const estimateGas = async () => {
    if (!canEstimate) return;
    setEstimating(true);
    setError(null);
    try {
      const signer = await wallet.provider.getSigner();
      const factory = new ethers.ContractFactory(artifact.abi, artifact.bytecode, signer);
      const supplyBig = BigInt(form.supply);
      // Constructor now takes just (name, symbol, supply, creator). LP is a
      // separate follow-up tx, so gas here is deploy-only.
      const deployTx = await factory.getDeployTransaction(
        form.name, symbolUpper, supplyBig, wallet.address
      );
      const [gasUnits, feeData] = await Promise.all([
        wallet.provider.estimateGas({ ...deployTx, from: wallet.address }),
        wallet.provider.getFeeData(),
      ]);
      const gasPrice = feeData.maxFeePerGas || feeData.gasPrice || 0n;
      const wei = gasUnits * gasPrice;
      setGasEstimate({
        eth: ethers.formatEther(wei),
        gasUnits: gasUnits.toString(),
        gwei: ethers.formatUnits(gasPrice, 'gwei'),
        lpEth: hasLpSupport(wallet.chainId) ? INITIAL_ETH : null,
      });
    } catch (e) {
      console.error(e);
      setError(e?.shortMessage || e?.message || 'Gas estimation failed');
    } finally {
      setEstimating(false);
    }
  };

  useEffect(() => {
    if (canEstimate) {
      const t = setTimeout(estimateGas, 500);
      return () => clearTimeout(t);
    } else {
      setGasEstimate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, form.supply, symbolUpper, wallet?.address, wallet?.chainId]);

  const launch = async () => {
    setError(null);
    setSuccess(null);
    if (!wallet) { try { await connect('metamask'); } catch (_) { /* user dismissed connect */ } return; }
    if (!hasLpSupport(wallet.chainId)) {
      toast.error('This network does not support auto-liquidity. Switch to Base or Base Sepolia to launch.');
      return;
    }
    if (!form.name.trim() || !symbolUpper || !(Number(form.supply) > 0)) {
      toast.error('Fill name, symbol and supply');
      return;
    }
    setStep('upload');
    try {
      let imageUrl = null;
      if (image?.file) {
        const r = await uploadImage(image.file);
        imageUrl = r.image_url;
      }
      setStep('generate');
      await new Promise((r) => setTimeout(r, 350));
      setStep('compile');
      await new Promise((r) => setTimeout(r, 350));

      setStep('deploy');
      const signer = await wallet.provider.getSigner();
      const factory = new ethers.ContractFactory(artifact.abi, artifact.bytecode, signer);
      const supplyBig = BigInt(form.supply);
      const net = networkOf(wallet.chainId);
      // Tx 1: deploy token (mints full supply to the contract itself)
      const contract = await factory.deploy(
        form.name, symbolUpper, supplyBig, wallet.address
      );
      const deployTx = contract.deploymentTransaction();
      await contract.waitForDeployment();
      const contractAddress = await contract.getAddress();

      // Tx 2: seed 80% supply into Uniswap V3 + burn LP NFT.
      // Two guardrails before we call seedLiquidity — these two issues caused
      // the "wallet never showed a second prompt" silent-fail in production:
      //   1. Wait for the RPC to index the new bytecode (avoid estimateGas
      //      returning "contract not deployed" for the freshly-deployed addr).
      //   2. Verify the wallet has enough ETH for the LP (0.01) + gas buffer.
      let lpTxHash = null;
      let seedFailed = false;
      setStep('seed');
      try {
        const indexed = await waitForBytecode(wallet.provider, contractAddress, { timeoutMs: 30000 });
        if (!indexed) throw new Error('RPC never indexed the new contract — please retry LP seeding in a moment.');
        const balCheck = await hasEnoughForSeed({ provider: wallet.provider, address: wallet.address });
        if (!balCheck.ok) throw new Error(`Insufficient ETH for LP seeding. Need ~0.012 ETH (0.01 pool + gas), have ${Number(ethers.formatEther(balCheck.balance)).toFixed(5)}.`);
        const seeded = await contract.seedLiquidity(
          net.weth, net.nfpm,
          { value: ethers.parseEther(INITIAL_ETH) }
        );
        await seeded.wait();
        lpTxHash = seeded.hash;
      } catch (seedErr) {
        console.error('[coinforge] seedLiquidity failed:', seedErr);
        seedFailed = true;
        setError(seedErr?.shortMessage || seedErr?.message || 'LP seeding failed');
      }

      const saved = await saveToken({
        name: form.name.trim(),
        symbol: symbolUpper,
        total_supply: form.supply,
        image_url: imageUrl,
        contract_address: contractAddress,
        creator_address: wallet.address,
        tx_hash: deployTx?.hash || null,
        chain_id: wallet.chainId,
        chain_name: net.name,
        explorer_url: deployTx?.hash ? txUrl(wallet.chainId, deployTx.hash) : null,
        lp_seeded: !seedFailed,
      });

      if (seedFailed) {
        // Deploy landed; LP didn't. Show a persistent recovery panel INSTEAD of
        // firing confetti/success — the coin is only half-launched.
        setStep(null);
        setPartial(saved);
        toast.error('Token deployed, but LP seeding failed', {
          description: 'Use the recovery panel below to retry or recover.',
          duration: 8000,
        });
        return;
      }

      setStep('done');
      setSuccess(saved);
      fireLaunchConfetti();
      playLaunchFanfare();
      toast.success(`${saved.symbol} deployed`);

      // Prompt the user's wallet to add the token so it shows up in their balance list.
      // This is a UX nicety; ignore any error (user may decline, or wallet may not support it).
      try {
        await wallet.rawProvider.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: contractAddress,
              symbol: symbolUpper,
              decimals: 18,
              image: imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`) : undefined,
            },
          },
        });
      } catch (_) {
        /* user declined or unsupported — silent */
      }

      setForm({ name: '', symbol: '', supply: '1000000' });
      setImage(null);
    } catch (e) {
      console.error(e);
      const msg = e?.shortMessage || e?.info?.error?.message || e?.message || 'Launch failed';
      setError(msg);
    }
  };

  const net = wallet ? networkOf(wallet.chainId) : null;
  const lpOn = wallet ? hasLpSupport(wallet.chainId) : false;
  const isOnBase = wallet && (wallet.chainId === 8453 || wallet.chainId === 84532);
  const isMainnet = wallet?.chainId === 1;

  // Switch (or add + switch) the wallet to any launch-capable chain.
  // Strategy: wallet_addEthereumChain first (acts as "add if missing + switch" in
  // modern wallets, and works around wallets that silently ignore switch requests).
  // Exception: mainnet — wallets refuse adding chainId 0x1, so switch directly.
  const switchToChain = async (targetId) => {
    if (!wallet?.rawProvider) {
      toast.error('Connect your wallet first');
      return;
    }
    const params = CHAIN_PARAMS[targetId];
    if (!params) {
      toast.error('Unsupported network');
      return;
    }
    const req = (method, p) => wallet.rawProvider.request({ method, params: p });
    const isRejection = (e) => e?.code === 4001 || /user rejected/i.test(e?.message || '');
    try {
      if (targetId === 1) {
        await req('wallet_switchEthereumChain', [{ chainId: params.chainId }]);
      } else {
        await req('wallet_addEthereumChain', [params]);
      }
      toast.success(`Switched to ${params.chainName}`);
    } catch (err) {
      console.warn('[coinforge] chain switch attempt failed:', err);
      if (isRejection(err)) {
        toast.error('You rejected the network switch');
        return;
      }
      // Chain already added but not active → try plain switch
      try {
        await req('wallet_switchEthereumChain', [{ chainId: params.chainId }]);
        toast.success(`Switched to ${params.chainName}`);
      } catch (err2) {
        console.error('[coinforge] wallet_switchEthereumChain failed:', err2);
        toast.error(
          isRejection(err2)
            ? 'You rejected the network switch'
            : (err2?.message || `Couldn't switch automatically — please switch to ${params.chainName} manually in your wallet.`)
        );
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      {wallet && !isOnBase && (
        <div data-testid="network-warning" className="mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#F472B6]/15 to-[#A78BFA]/15 border border-[#F472B6]/40">
          <AlertTriangle className="w-5 h-5 text-[#F472B6] shrink-0" />
          <div className="flex-1 text-sm">
            {!lpOn ? (
              <>
                <p className="font-display font-semibold text-white">
                  Launching is blocked on {net.name} — no Uniswap V3 support.
                </p>
                <p className="text-forge-muted text-xs mt-0.5">
                  Without auto-liquidity, 80% of your supply would be burned instead of paired. Switch to Base to launch.
                </p>
              </>
            ) : (
              <>
                <p className="font-display font-semibold text-white">
                  You&apos;re on {net.name}. {isMainnet ? 'Gas will be ~1000× more expensive than Base.' : 'Base is cheaper and the default target.'}
                </p>
                <p className="text-forge-muted text-xs mt-0.5">
                  You <span className="text-white">can</span> still launch here, but we recommend switching to Base Sepolia (testnet, free) or Base Mainnet (production).
                </p>
              </>
            )}
          </div>
          <button data-testid="switch-to-base-btn" onClick={() => switchToChain(DEFAULT_CHAIN_ID)} className="cf-btn-primary text-sm !py-2 !px-4 shrink-0">
            <ArrowRightLeft className="w-4 h-4" /> Switch to Base Sepolia
          </button>
        </div>
      )}      <div className="grid lg:grid-cols-12 gap-10 items-start">
        {/* Hero */}
        <div className="lg:col-span-7 space-y-6">
          <span className="cf-chip cf-float"><Zap className="w-3 h-3" /> Launch in 2 clicks</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight">
            Forge your own <span className="cf-gradient-text">crypto coin.</span>
          </h1>
          <p className="text-forge-muted max-w-xl text-lg leading-relaxed">
            Mint an ERC-20 token straight from your wallet. A hard-coded 1.5% tax lives inside every transfer forever —
            <span className="text-white"> 0.5% flows back to you</span> as passive income. No smart-contract skills required.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <CreatorEconomicsModal />
            <span className="text-xs text-forge-muted">
              Platform wallet · <span className="font-code">0x157d…3eC</span>
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-4 max-w-md">
            <Stat label="Total tax" value="1.5%" tone="cyan" />
            <Stat label="To creator" value="0.5%" tone="violet" />
            <Stat label="To platform" value="1.0%" tone="pink" />
          </div>
        </div>

        {/* Launch Form */}
        <div className="lg:col-span-5">
          <div className="cf-card p-6 sm:p-7">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs text-forge-muted uppercase tracking-widest font-medium">Launchpad</p>
                <h2 className="font-display text-xl font-semibold mt-1">Launch your token</h2>
              </div>
              {wallet && <span className="cf-chip">{net.short}</span>}
            </div>
            <div className="mb-5 space-y-3">
              <Field label="Network" hint={wallet ? 'Changing prompts a wallet switch' : 'Connect your wallet to choose'}>
                <select
                  data-testid="chain-selector"
                  className="cf-input"
                  disabled={!wallet}
                  value={LAUNCH_CHAINS.some((c) => c.id === wallet?.chainId) ? wallet.chainId : ''}
                  onChange={(e) => e.target.value && switchToChain(Number(e.target.value))}
                >
                  {!LAUNCH_CHAINS.some((c) => c.id === wallet?.chainId) && (
                    <option value="">{wallet ? `${net.name} (unsupported)` : 'Connect wallet first'}</option>
                  )}
                  {LAUNCH_CHAINS.map((c) => (
                    <option key={c.id} value={c.id}>{c.label} — {c.note}</option>
                  ))}
                </select>
              </Field>
              {isMainnet && (
                <div data-testid="mainnet-gas-warning" className="rounded-xl bg-[#FF6B7A]/10 border border-[#FF6B7A]/40 p-3 text-xs text-[#FF6B7A] flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Ethereum mainnet gas is ~1000× more expensive than Base — launching here can cost $50–100+ in gas alone. We recommend Base.</span>
                </div>
              )}
            </div>
            <div className="mb-5">
              <p className="text-xs text-forge-muted uppercase tracking-wider font-medium mb-2 flex items-center gap-1.5">
                <Wand2 className="w-3.5 h-3.5" /> Quick presets
              </p>
              <div className="flex flex-wrap gap-2" data-testid="quick-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.symbol}
                    type="button"
                    data-testid={`preset-${p.symbol}`}
                    onClick={() => applyPreset(p)}
                    className="cf-chip !cursor-pointer hover:border-[#7DF9FF]/50 hover:text-[#7DF9FF] transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-5">
              <div className="space-y-4">
                <Field label="Token name" hint="e.g. Doge Forge">
                  <input
                    data-testid="token-name-input"
                    className="cf-input"
                    placeholder="My Coin"
                    value={form.name}
                    onChange={set('name')}
                    maxLength={48}
                  />
                </Field>
                <Field label="Ticker symbol" hint="3–11 chars, A–Z 0–9">
                  <input
                    data-testid="token-symbol-input"
                    className="cf-input uppercase tracking-wider"
                    placeholder="DOGE"
                    value={symbolUpper}
                    onChange={set('symbol')}
                  />
                </Field>
                <Field label="Total supply" hint="Whole tokens (18 decimals on-chain)">
                  <input
                    data-testid="token-supply-input"
                    className="cf-input"
                    type="number"
                    min="1"
                    placeholder="1000000"
                    value={form.supply}
                    onChange={set('supply')}
                  />
                </Field>
              </div>
              <Field label="Logo">
                <ImageDropzone value={image} onChange={setImage} />
              </Field>
            </div>

            {/* Cost breakdown — always visible */}
            <div className="mt-5 rounded-2xl bg-gradient-to-br from-[#7DF9FF]/8 via-[#A78BFA]/6 to-transparent border border-white/10 p-4">
              <p className="text-xs text-forge-muted uppercase tracking-wider font-medium mb-3 flex items-center gap-2">
                <Fuel className="w-3.5 h-3.5" /> Total launch cost
              </p>
              <div className="space-y-1.5 text-sm">
                <CostRow label="Deploy gas" value={
                  !wallet ? 'connect wallet' : estimating ? 'calculating…' : gasEstimate ? `~ ${Number(gasEstimate.eth).toFixed(6)} ETH` : 'fill fields above'
                } />
                {lpOn && (
                  <>
                    <CostRow label="Seed-LP tx gas" value="~ 0.001 ETH (est.)" />
                    <CostRow label="Liquidity capital" value={`${INITIAL_ETH} ETH`} highlight />
                  </>
                )}
                <div className="border-t border-white/5 pt-2 mt-2 flex items-center justify-between">
                  <span className="text-white font-display font-semibold">You pay</span>
                  <span data-testid="total-cost" className="font-display font-bold text-lg cf-gradient-text">
                    {lpOn ? `~ ${INITIAL_ETH} ETH + gas` : gasEstimate ? `~ ${Number(gasEstimate.eth).toFixed(4)} ETH` : '—'}
                  </span>
                </div>
                <p className="text-[11px] text-forge-muted pt-1">
                  {lpOn ? 'The 0.01 ETH becomes liquidity in your pool — you keep exposure to it.' : 'Deploy gas only. No liquidity pool on this network.'}
                </p>
              </div>
            </div>

            <button
              data-testid="launch-token-btn"
              onClick={launch}
              disabled={(step && step !== 'done' && !error) || (wallet && !lpOn)}
              className="cf-btn-primary mt-5 w-full !py-4 text-base"
            >
              <Sparkles className="w-5 h-5" />
              {!wallet
                ? 'Connect & launch'
                : !lpOn
                  ? 'Unsupported network'
                  : 'Launch token'}
            </button>
            <p className="text-xs text-forge-muted mt-3 text-center">
              {wallet && !lpOn
                ? 'CoinForge only launches on Base networks. Use the banner above to switch.'
                : 'By launching you accept the immutable on-chain 1.5% tax.'}
            </p>
          </div>
        </div>
      </div>

      <ProgressModal
        open={!!step}
        currentStep={step}
        error={error}
        onClose={() => { setStep(null); setError(null); }}
      />
      <SuccessModal open={!!success} onClose={() => setSuccess(null)} token={success} />
      {partial && (
        <div data-testid="partial-launch-recovery" className="mt-10 max-w-3xl mx-auto space-y-3">
          <p className="text-xs uppercase tracking-widest text-forge-muted font-medium">Half-launched · finish or recover</p>
          <LpRecoveryPanel
            contractAddress={partial.contract_address}
            chainId={partial.chain_id}
            creatorAddress={partial.creator_address}
            onSeeded={() => { setPartial(null); setSuccess(partial); fireLaunchConfetti(); playLaunchFanfare(); }}
          />
          <button
            type="button"
            data-testid="dismiss-partial-launch"
            onClick={() => setPartial(null)}
            className="text-xs text-forge-muted hover:text-white transition-colors underline"
          >
            Dismiss — I&apos;ll finish this from the token page
          </button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block space-y-2">
    <span className="block text-xs text-forge-muted uppercase tracking-wider font-medium">{label}</span>
    {children}
    {hint && <span className="block text-xs text-forge-muted/70">{hint}</span>}
  </label>
);

const CostRow = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-forge-muted">{label}</span>
    <span className={`font-code text-xs ${highlight ? 'text-[#A78BFA]' : 'text-white'}`}>{value}</span>
  </div>
);

const Stat = ({ label, value, tone }) => {
  const tones = {
    cyan: 'from-[#7DF9FF]/20 to-[#7DF9FF]/5 border-[#7DF9FF]/30',
    violet: 'from-[#A78BFA]/20 to-[#A78BFA]/5 border-[#A78BFA]/30',
    pink: 'from-[#F472B6]/20 to-[#F472B6]/5 border-[#F472B6]/30',
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tones[tone]} border p-3.5`}>
      <p className="text-xs text-forge-muted font-medium">{label}</p>
      <p className="font-display text-2xl font-bold mt-1 text-white">{value}</p>
    </div>
  );
};

export default Dashboard;

